'use client';

/**
 * LandingFeaturedMarketplace — landing section showing top 3 products
 * by sales in the last 30 days.
 *
 * PR #1444 (v124.2): Founder directive — "agreguemos el marketplace
 * con acceso desde la landing principal para que sea más visible".
 *
 * Fetches `GET /api/market/products/featured?limit=3` (PUBLIC endpoint
 * added in this PR). Renders a 3-column grid of `<ProductCard>` (existing
 * reusable component from `components/market/product-card.tsx`).
 *
 * UX:
 *   - Heading: "Marketplace destacado"
 *   - Subtitle: brief copy motivating click
 *   - Grid: 1 col mobile, 2 col tablet, 3 col desktop
 *   - CTA button: "Ver todo el marketplace" → `/market`
 *
 * Empty state: returns null (don't render section if 0 featured — landing
 * shows nothing, no "no hay productos" message — keeps landing clean).
 *
 * Loading state: skeleton loader (3 cards shimmer) — keeps layout stable.
 *
 * Error state: silent fail + return null (don't break the landing page if
 * featured API fails — graceful degradation).
 *
 * Anti-P-043 (AbortController): in-flight fetch cancels on unmount.
 * Anti-P-041 (no `as any`): uses `PublicProductResponse` typed response.
 * Anti-P-042 (no `console.*`): silent fail.
 *
 * Spec ref: PR #1444 + SPEC-081 Fase 3 §6.5 (Product card), SPEC-NEGOCIO
 *           §10.4 (Portal de Vendedores).
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import Link from 'next/link';
import { motion } from 'framer-motion';
import { ArrowRight, ShoppingBag } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { ProductCard } from '@/components/market/product-card';
import { apiFetch, ApiClientError } from '@/lib/api-client';
import type { PublicProductResponse } from '@profileia/shared';


export function LandingFeaturedMarketplace() {
  const [products, setProducts] = useState<PublicProductResponse[] | null>(null);
  const abortRef = useRef<AbortController | null>(null);

  const fetchFeatured = useCallback(async (): Promise<void> => {
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    try {
      // NOTE: apiFetch auto-unwraps `.data` by default (see fetch-client.ts).
      // So `res` IS the array, NOT `{ data: array }`. Don't access res.data.
      const res = await apiFetch<PublicProductResponse[]>(
        '/market/products/featured?limit=3',
        { signal: controller.signal },
      );
      setProducts(res ?? []);
    } catch (err) {
      // Silent fail — don't break landing if API fails.
      // AbortError (unmount) is expected — don't log.
      if (err instanceof ApiClientError && err.status === 0) return;
      if (err instanceof DOMException && err.name === 'AbortError') return;
      setProducts([]);
    }
  }, []);

  useEffect(() => {
    void fetchFeatured();
    return () => {
      abortRef.current?.abort();
    };
  }, [fetchFeatured]);

  // Loading state — 3 skeleton cards
  if (products === null) {
    return (
      <section className="py-20 bg-muted/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold mb-3">
              Marketplace destacado
            </h2>
            <p className="text-muted-foreground">Cargando productos…</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {[0, 1, 2].map((i) => (
              <div
                key={i}
                className="h-72 rounded-2xl bg-card animate-pulse border border-border"
              />
            ))}
          </div>
        </div>
      </section>
    );
  }

  // Empty state — don't render section (no featured products)
  if (products.length === 0) return null;

  return (
    <section className="py-20 bg-muted/30">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-100px' }}
          className="text-center mb-12"
        >
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary/10 text-primary mb-4">
            <ShoppingBag className="size-4" />
            <span className="text-sm font-medium">Marketplace</span>
          </div>
          <h2 className="text-3xl sm:text-4xl font-bold mb-3">
            Marketplace destacado
          </h2>
          <p className="text-muted-foreground max-w-2xl mx-auto">
            Productos más vendidos en los últimos 30 días por comercios
            verificados de Profileia.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 mb-10">
          {products.map((product) => (
            <ProductCard key={product.productId} product={product} />
          ))}
        </div>

        <div className="text-center">
          <Button asChild size="lg">
            <Link href="/market">
              Ver todo el marketplace
              <ArrowRight className="size-4 ml-2" />
            </Link>
          </Button>
        </div>
      </div>
    </section>
  );
}
